<template>
  <header class="site-header">
    <div class="site-header__bar">
      <Link href="/" class="site-header__logo">
        <img src="/img/home/brand-logo.webp" alt="Back to Work Connect" width="47" height="52" />
      </Link>

      <nav class="site-header__nav" aria-label="Main">
        <template v-for="item in mainLinks">
          <a v-if="item.external" :key="item.label" :href="item.href">{{ item.label }}</a>
          <Link v-else :key="item.label" :href="item.href">{{ item.label }}</Link>
        </template>
        <button v-if="!isLoggedIn" type="button" @click="$emit('login')">Login</button>
        <template v-else>
          <Link v-for="item in accountLinks" :key="item.label" :href="item.href">{{ item.label }}</Link>
          <Link href="/logout" method="delete" as="button">Logout</Link>
        </template>
      </nav>

      <div class="site-header__compact">
        <button v-if="!isLoggedIn" type="button" class="site-header__login" @click="$emit('login')">Login</button>
        <Link v-else-if="accountLinks.length" :href="accountLinks[0].href" class="site-header__login">{{ accountLinks[0].label }}</Link>
        <button
          ref="burger"
          type="button"
          class="site-header__burger"
          aria-label="Open menu"
          aria-controls="site-menu"
          :aria-expanded="menuOpen ? 'true' : 'false'"
          @click="openMenu"
        >
          <span></span><span></span><span></span>
        </button>
      </div>
    </div>

    <p class="site-header__tagline">Ireland’s Career Hub for Midlife Returners and Career Changers</p>

    <div
      v-if="menuOpen"
      id="site-menu"
      ref="menu"
      class="site-menu"
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
      @keydown="onMenuKeydown"
    >
      <div class="site-menu__top">
        <img src="/img/home/brand-logo-white.webp" alt="Back to Work Connect" width="40" height="44" />
        <button ref="close" type="button" class="site-menu__close" aria-label="Close menu" @click="closeMenu">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </div>
      <nav class="site-menu__nav" aria-label="Main" @click="closeMenu">
        <template v-for="item in mainLinks">
          <a v-if="item.external" :key="item.label" :href="item.href">{{ item.label }}<span aria-hidden="true">→</span></a>
          <Link v-else :key="item.label" :href="item.href">{{ item.label }}<span aria-hidden="true">→</span></Link>
        </template>
      </nav>
      <div class="site-menu__account">
        <button v-if="!isLoggedIn" type="button" class="site-menu__primary" @click="loginFromMenu">Login</button>
        <template v-else>
          <Link v-for="item in accountLinks" :key="item.label" :href="item.href" class="site-menu__primary" @click="closeMenu">{{ item.label }}</Link>
          <Link href="/logout" method="delete" as="button" class="site-menu__secondary" @click="closeMenu">Logout</Link>
        </template>
      </div>
    </div>
  </header>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue'

export default {
  components: {
    Link,
  },
  props: {
    auth: Object,
  },
  data() {
    return {
      menuOpen: false,
    }
  },
  computed: {
    roles() {
      return (this.auth && this.auth.user && this.auth.user.roles) || []
    },
    isLoggedIn() {
      return !!(this.auth && this.auth.user)
    },
    // Same role rules as the main site header.
    mainLinks() {
      const is = r => this.roles.includes(r)
      return [
        { label: 'Jobs', href: '/search/job', show: !is('course_provider') },
        { label: 'Courses', href: '/search/course', show: !is('employer') },
        { label: 'Events', href: '/events', show: !is('course_provider') && !is('employer') },
        { label: 'Career Hub', href: '/careerhub/', external: true, show: true },
      ].filter(item => item.show)
    },
    accountLinks() {
      const is = r => this.roles.includes(r)
      return [
        { label: 'Admin', href: '/admin', show: is('admin') },
        { label: 'My Jobs', href: '/employer/dashboard', show: is('employer') },
        { label: 'My Courses', href: '/provider/dashboard', show: is('course_provider') },
        { label: 'My Profile', href: '/dashboard', show: is('user') },
      ].filter(item => item.show)
    },
  },
  mounted() {
    // The sheet only exists on phones; if the window grows past that while it
    // is open, close it rather than leave the page scroll-locked.
    this.wideQuery = window.matchMedia('(min-width: 640px)')
    this.onWide = e => e.matches && this.closeMenu()
    this.wideQuery.addEventListener
      ? this.wideQuery.addEventListener('change', this.onWide)
      : this.wideQuery.addListener(this.onWide)
  },
  beforeDestroy() {
    if (this.wideQuery) {
      this.wideQuery.removeEventListener
        ? this.wideQuery.removeEventListener('change', this.onWide)
        : this.wideQuery.removeListener(this.onWide)
    }
    this.lockScroll(false)
  },
  methods: {
    openMenu() {
      this.menuOpen = true
      this.lockScroll(true)
      this.$nextTick(() => this.$refs.close && this.$refs.close.focus())
    },
    closeMenu() {
      if (!this.menuOpen) return
      this.menuOpen = false
      this.lockScroll(false)
      this.$nextTick(() => this.$refs.burger && this.$refs.burger.focus())
    },
    loginFromMenu() {
      this.closeMenu()
      this.$emit('login')
    },
    lockScroll(lock) {
      document.documentElement.style.overflow = lock ? 'hidden' : ''
    },
    onMenuKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault()
        this.closeMenu()
        return
      }
      if (e.key !== 'Tab') return
      // Keep focus inside the sheet while it is open.
      const focusable = this.$refs.menu.querySelectorAll('a[href], button:not([disabled])')
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    },
  },
}
</script>

<style scoped>
.site-header {
  font-family: 'Montserrat', sans-serif;
  line-height: normal;
  -webkit-font-smoothing: antialiased;
}
.site-header__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  padding: 20px clamp(20px, 4vw, 64px) 0;
}
.site-header__logo {
  display: block;
  flex: 0 0 auto;
}
.site-header__logo img {
  display: block;
  height: 52px;
  width: auto;
}
.site-header__nav {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: clamp(18px, 3vw, 36px);
  white-space: nowrap;
}
.site-header__nav a,
.site-header__nav button {
  padding: 0;
  border: 0;
  background: none;
  font: 700 17px 'Montserrat', sans-serif;
  color: #560354;
  text-decoration: none;
  cursor: pointer;
}
.site-header__nav a:hover,
.site-header__nav button:hover {
  color: #7A0B74;
}
.site-header__tagline {
  margin: 10px 0 0;
  padding: 0 clamp(20px, 4vw, 64px);
  font-size: 15px;
  font-weight: 700;
  color: #560354;
}
.site-header__compact {
  display: none;
}

/* Phones: logo, Login and a menu button; the links move into a sheet. */
@media (max-width: 639px) {
  .site-header__bar {
    flex-wrap: nowrap;
    gap: 16px;
    padding: 14px 20px;
    border-bottom: 1px solid #EFEAE4;
  }
  .site-header__logo img {
    height: 44px;
  }
  .site-header__nav,
  .site-header__tagline {
    display: none;
  }
  .site-header__compact {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .site-header__login {
    display: flex;
    align-items: center;
    min-height: 44px;
    padding: 0 4px;
    border: 0;
    background: none;
    font: 700 14px 'Montserrat', sans-serif;
    color: #560354;
    text-decoration: none;
    cursor: pointer;
  }
  .site-header__burger {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    width: 44px;
    height: 44px;
    padding: 0;
    border: 0;
    border-radius: 12px;
    background: #560354;
    cursor: pointer;
  }
  .site-header__burger span {
    display: block;
    width: 20px;
    height: 2px;
    border-radius: 2px;
    background: #FFFFFF;
  }
}

.site-menu {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  padding: 14px 20px 28px;
  overflow-y: auto;
  background: #560354;
  color: #FFFFFF;
}
.site-menu__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.site-menu__top img {
  display: block;
  height: 44px;
  width: auto;
}
.site-menu__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 12px;
  background: rgba(255, 255, 255, .14);
  color: #FFFFFF;
  cursor: pointer;
}
.site-menu__nav {
  display: flex;
  flex-direction: column;
  margin-top: 28px;
}
.site-menu__nav a {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 60px;
  border-bottom: 1px solid rgba(255, 255, 255, .2);
  font-size: 22px;
  font-weight: 700;
  color: #FFFFFF;
  text-decoration: none;
}
.site-menu__nav a span {
  opacity: .7;
}
.site-menu__account {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: auto;
  padding-top: 32px;
}
.site-menu__primary,
.site-menu__secondary {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 50px;
  border-radius: 999px;
  font: 700 17px 'Montserrat', sans-serif;
  text-decoration: none;
  cursor: pointer;
}
.site-menu__primary {
  border: 0;
  background: #FFFFFF;
  color: #560354;
}
.site-menu__secondary {
  border: 1px solid rgba(255, 255, 255, .6);
  background: transparent;
  color: #FFFFFF;
}
.site-menu a:focus-visible,
.site-menu button:focus-visible,
.site-header__bar a:focus-visible,
.site-header__bar button:focus-visible {
  outline: 3px solid #C99BC7;
  outline-offset: 2px;
}
</style>
